import React, { useRef, useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { fabric } from 'fabric';

/**
 * PhotoAnnotator — Fullscreen annotation tool for workorder photos.
 * 
 * Uses Fabric.js for canvas-based drawing on top of a captured photo.
 * Designed for tablet use on the shop floor (large touch targets, minimal UI).
 * 
 * Props:
 *   imageUrl    — Blob URL or data URL of the captured photo
 *   onSave      — (annotatedBlob: Blob) => void — called with the final PNG
 *   onCancel    — () => void — close without saving
 *   onSkip      — () => void — save original without annotation (optional)
 */
export default function PhotoAnnotator({ imageUrl, onSave, onCancel, onSkip }) {
    const canvasRef = useRef(null);
    const fabricRef = useRef(null);
    const containerRef = useRef(null);

    const [activeTool, setActiveTool] = useState('pen'); // pen | circle | arrow | text
    const [activeColor, setActiveColor] = useState('#ef4444'); // red default
    const [brushWidth, setBrushWidth] = useState(4);
    const [saving, setSaving] = useState(false);
    const [canvasReady, setCanvasReady] = useState(false);

    // History for undo
    const historyRef = useRef([]);

    // Colors — industrial: red for defects, yellow for warnings, white for readability on dark
    const COLORS = [
        { value: '#ef4444', label: 'Rood', bg: 'bg-red-500' },
        { value: '#eab308', label: 'Geel', bg: 'bg-yellow-500' },
        { value: '#ffffff', label: 'Wit', bg: 'bg-white' },
        { value: '#000000', label: 'Zwart', bg: 'bg-black border border-white/30' },
    ];

    const TOOLS = [
        { id: 'pen', icon: '✏️', label: 'Pen' },
        { id: 'circle', icon: '⭕', label: 'Cirkel' },
        { id: 'arrow', icon: '➡️', label: 'Pijl' },
        { id: 'text', icon: 'Aa', label: 'Tekst' },
    ];

    // Initialize Fabric canvas with the image
    useEffect(() => {
        if (!canvasRef.current || !imageUrl) return;

        const canvas = new fabric.Canvas(canvasRef.current, {
            isDrawingMode: true,
            selection: false,
            backgroundColor: '#000',
        });

        fabricRef.current = canvas;

        // Load the photo as background
        fabric.Image.fromURL(imageUrl, (img) => {
            if (!containerRef.current) return;

            const containerWidth = containerRef.current.clientWidth;
            const containerHeight = containerRef.current.clientHeight - 120; // Leave room for toolbar

            const scale = Math.min(
                containerWidth / img.width,
                containerHeight / img.height,
                1 // Don't upscale
            );

            const canvasWidth = Math.floor(img.width * scale);
            const canvasHeight = Math.floor(img.height * scale);

            canvas.setWidth(canvasWidth);
            canvas.setHeight(canvasHeight);

            canvas.setBackgroundImage(img, canvas.renderAll.bind(canvas), {
                scaleX: scale,
                scaleY: scale,
                originX: 'left',
                originY: 'top',
            });

            // Store original dimensions for export
            canvas._originalWidth = img.width;
            canvas._originalHeight = img.height;
            canvas._scaleFactor = scale;

            setCanvasReady(true);
        }, { crossOrigin: 'anonymous' });

        // Setup free drawing brush
        canvas.freeDrawingBrush = new fabric.PencilBrush(canvas);
        canvas.freeDrawingBrush.color = '#ef4444';
        canvas.freeDrawingBrush.width = 4;

        return () => {
            canvas.dispose();
            fabricRef.current = null;
        };
    }, [imageUrl]);

    // Update brush when tool/color/width changes
    useEffect(() => {
        const canvas = fabricRef.current;
        if (!canvas) return;

        canvas.isDrawingMode = activeTool === 'pen';

        if (activeTool === 'pen' && canvas.freeDrawingBrush) {
            canvas.freeDrawingBrush.color = activeColor;
            canvas.freeDrawingBrush.width = brushWidth;
        }
    }, [activeTool, activeColor, brushWidth]);

    // Shape drawing (circle, arrow)
    useEffect(() => {
        const canvas = fabricRef.current;
        if (!canvas || activeTool === 'pen') return;

        let isDown = false;
        let startX = 0;
        let startY = 0;
        let activeShape = null;

        const handleMouseDown = (opt) => {
            if (activeTool === 'text') {
                // Add text at click position
                const pointer = canvas.getPointer(opt.e);
                const text = new fabric.IText('Tekst', {
                    left: pointer.x,
                    top: pointer.y,
                    fontSize: Math.max(16, Math.round(24 / (canvas._scaleFactor || 1))),
                    fill: activeColor,
                    fontFamily: 'Arial',
                    fontWeight: 'bold',
                    editable: true,
                    stroke: activeColor === '#ffffff' || activeColor === '#eab308' ? '#000' : null,
                    strokeWidth: activeColor === '#ffffff' || activeColor === '#eab308' ? 1 : 0,
                });
                canvas.add(text);
                canvas.setActiveObject(text);
                text.enterEditing();
                // Push to history
                historyRef.current.push({ type: 'add', object: text });
                return;
            }

            isDown = true;
            const pointer = canvas.getPointer(opt.e);
            startX = pointer.x;
            startY = pointer.y;

            if (activeTool === 'circle') {
                activeShape = new fabric.Ellipse({
                    left: startX,
                    top: startY,
                    rx: 0,
                    ry: 0,
                    fill: 'transparent',
                    stroke: activeColor,
                    strokeWidth: brushWidth,
                    selectable: false,
                    evented: false,
                });
                canvas.add(activeShape);
            } else if (activeTool === 'arrow') {
                activeShape = new fabric.Line([startX, startY, startX, startY], {
                    stroke: activeColor,
                    strokeWidth: brushWidth,
                    selectable: false,
                    evented: false,
                });
                canvas.add(activeShape);
            }
        };

        const handleMouseMove = (opt) => {
            if (!isDown || !activeShape) return;
            const pointer = canvas.getPointer(opt.e);

            if (activeTool === 'circle') {
                const rx = Math.abs(pointer.x - startX) / 2;
                const ry = Math.abs(pointer.y - startY) / 2;
                activeShape.set({
                    rx,
                    ry,
                    left: Math.min(startX, pointer.x),
                    top: Math.min(startY, pointer.y),
                });
            } else if (activeTool === 'arrow') {
                activeShape.set({ x2: pointer.x, y2: pointer.y });
            }

            canvas.renderAll();
        };

        const handleMouseUp = () => {
            if (!isDown) return;
            isDown = false;

            if (activeShape) {
                // Add arrowhead for arrow tool
                if (activeTool === 'arrow') {
                    const x1 = activeShape.x1;
                    const y1 = activeShape.y1;
                    const x2 = activeShape.x2;
                    const y2 = activeShape.y2;

                    const angle = Math.atan2(y2 - y1, x2 - x1);
                    const headLength = 15;

                    const arrowHead = new fabric.Triangle({
                        left: x2,
                        top: y2,
                        width: headLength,
                        height: headLength,
                        fill: activeColor,
                        angle: (angle * 180) / Math.PI + 90,
                        originX: 'center',
                        originY: 'center',
                        selectable: false,
                        evented: false,
                    });
                    canvas.add(arrowHead);

                    // Group line + head for undo
                    historyRef.current.push({ type: 'add', objects: [activeShape, arrowHead] });
                } else {
                    historyRef.current.push({ type: 'add', object: activeShape });
                }

                activeShape = null;
            }
        };

        canvas.on('mouse:down', handleMouseDown);
        canvas.on('mouse:move', handleMouseMove);
        canvas.on('mouse:up', handleMouseUp);

        return () => {
            canvas.off('mouse:down', handleMouseDown);
            canvas.off('mouse:move', handleMouseMove);
            canvas.off('mouse:up', handleMouseUp);
        };
    }, [activeTool, activeColor, brushWidth]);

    // Track pen strokes for undo
    useEffect(() => {
        const canvas = fabricRef.current;
        if (!canvas) return;

        const handlePathCreated = (opt) => {
            historyRef.current.push({ type: 'add', object: opt.path });
        };

        canvas.on('path:created', handlePathCreated);
        return () => canvas.off('path:created', handlePathCreated);
    }, [canvasReady]);

    // Undo last action
    const handleUndo = useCallback(() => {
        const canvas = fabricRef.current;
        if (!canvas || historyRef.current.length === 0) return;

        const lastAction = historyRef.current.pop();

        if (lastAction.objects) {
            // Multi-object (arrow = line + head)
            lastAction.objects.forEach(obj => canvas.remove(obj));
        } else if (lastAction.object) {
            canvas.remove(lastAction.object);
        }

        canvas.renderAll();
    }, []);

    // Clear all annotations
    const handleClearAll = useCallback(() => {
        const canvas = fabricRef.current;
        if (!canvas) return;

        const objects = canvas.getObjects();
        objects.forEach(obj => canvas.remove(obj));
        historyRef.current = [];
        canvas.renderAll();
    }, []);

    // Export annotated image
    const handleSave = useCallback(async () => {
        const canvas = fabricRef.current;
        if (!canvas || saving) return;

        setSaving(true);

        try {
            // Deselect any active object to hide selection handles
            canvas.discardActiveObject();
            canvas.renderAll();

            // Export at original resolution for quality
            const multiplier = 1 / (canvas._scaleFactor || 1);

            const dataUrl = canvas.toDataURL({
                format: 'png',
                quality: 1,
                multiplier,
            });

            // Convert data URL to Blob
            const response = await fetch(dataUrl);
            const blob = await response.blob();

            onSave(blob);
        } catch (err) {
            console.error('[PhotoAnnotator] Export failed:', err);
            setSaving(false);
        }
    }, [saving, onSave]);

    // Keyboard shortcuts
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'z' && (e.ctrlKey || e.metaKey)) {
                e.preventDefault();
                handleUndo();
            } else if (e.key === 'Escape') {
                onCancel();
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [handleUndo, onCancel]);

    return createPortal(
        <div
            ref={containerRef}
            className="fixed z-[100000] flex flex-col"
            style={{
                top: 0, left: 0, right: 0, bottom: 0,
                margin: 0, padding: 0, position: 'fixed',
                backgroundColor: '#0f172a',
            }}
        >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-2 bg-slate-900 border-b border-slate-700 shrink-0">
                <div className="flex items-center gap-3">
                    <span className="text-xl">✏️</span>
                    <h3 className="text-white font-bold text-sm uppercase tracking-wider">
                        Foto Annotatie
                    </h3>
                </div>
                <div className="flex items-center gap-2">
                    {onSkip && (
                        <button
                            onClick={onSkip}
                            className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-slate-300 text-sm font-bold rounded-lg transition-colors"
                            type="button"
                        >
                            Overslaan
                        </button>
                    )}
                    <button
                        onClick={onCancel}
                        className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white text-sm font-bold rounded-lg transition-colors"
                        type="button"
                    >
                        Annuleren
                    </button>
                    <button
                        onClick={handleSave}
                        disabled={saving || !canvasReady}
                        className="px-6 py-2 bg-green-600 hover:bg-green-500 text-white text-sm font-black rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
                        type="button"
                    >
                        {saving ? (
                            <span className="flex items-center gap-2">
                                <span className="loading loading-spinner loading-xs"></span>
                                Opslaan...
                            </span>
                        ) : (
                            '✓ Opslaan'
                        )}
                    </button>
                </div>
            </div>

            {/* Canvas Area */}
            <div className="flex-1 flex items-center justify-center overflow-hidden bg-black/50 min-h-0">
                {!canvasReady && (
                    <div className="absolute inset-0 flex items-center justify-center z-10">
                        <div className="text-center">
                            <div className="w-12 h-12 mx-auto mb-4 rounded-full border-4 border-t-blue-500 border-blue-500/30 animate-spin"></div>
                            <p className="text-slate-400 text-sm">Foto laden...</p>
                        </div>
                    </div>
                )}
                <canvas ref={canvasRef} />
            </div>

            {/* Toolbar */}
            <div className="shrink-0 bg-slate-900 border-t border-slate-700 px-4 py-3">
                <div className="flex items-center justify-between gap-4 max-w-2xl mx-auto">
                    {/* Tools */}
                    <div className="flex items-center gap-1">
                        {TOOLS.map(tool => (
                            <button
                                key={tool.id}
                                onClick={() => setActiveTool(tool.id)}
                                className={`flex items-center gap-1.5 px-3 py-2.5 rounded-lg font-bold text-sm transition-all ${activeTool === tool.id
                                        ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/30'
                                        : 'bg-slate-800 text-slate-400 hover:bg-slate-700 hover:text-white'
                                    }`}
                                type="button"
                                title={tool.label}
                            >
                                <span className="text-lg">{tool.icon}</span>
                                <span className="hidden sm:inline">{tool.label}</span>
                            </button>
                        ))}
                    </div>

                    {/* Colors */}
                    <div className="flex items-center gap-2">
                        {COLORS.map(color => (
                            <button
                                key={color.value}
                                onClick={() => setActiveColor(color.value)}
                                className={`w-8 h-8 rounded-full transition-all ${color.bg} ${activeColor === color.value
                                        ? 'ring-2 ring-blue-400 ring-offset-2 ring-offset-slate-900 scale-110'
                                        : 'opacity-60 hover:opacity-100'
                                    }`}
                                type="button"
                                title={color.label}
                            />
                        ))}
                    </div>

                    {/* Brush Size */}
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => setBrushWidth(3)}
                            className={`w-8 h-8 rounded-lg flex items-center justify-center ${brushWidth === 3 ? 'bg-blue-600' : 'bg-slate-800 hover:bg-slate-700'}`}
                            type="button"
                            title="Dun"
                        >
                            <div className="w-1 h-1 rounded-full bg-white"></div>
                        </button>
                        <button
                            onClick={() => setBrushWidth(6)}
                            className={`w-8 h-8 rounded-lg flex items-center justify-center ${brushWidth === 6 ? 'bg-blue-600' : 'bg-slate-800 hover:bg-slate-700'}`}
                            type="button"
                            title="Normaal"
                        >
                            <div className="w-2 h-2 rounded-full bg-white"></div>
                        </button>
                        <button
                            onClick={() => setBrushWidth(10)}
                            className={`w-8 h-8 rounded-lg flex items-center justify-center ${brushWidth === 10 ? 'bg-blue-600' : 'bg-slate-800 hover:bg-slate-700'}`}
                            type="button"
                            title="Dik"
                        >
                            <div className="w-3 h-3 rounded-full bg-white"></div>
                        </button>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2">
                        <button
                            onClick={handleUndo}
                            disabled={historyRef.current.length === 0}
                            className="px-3 py-2.5 bg-slate-800 hover:bg-slate-700 text-slate-300 text-sm font-bold rounded-lg transition-colors disabled:opacity-30"
                            type="button"
                            title="Ongedaan maken (Ctrl+Z)"
                        >
                            ↩️ Undo
                        </button>
                        <button
                            onClick={handleClearAll}
                            className="px-3 py-2.5 bg-red-900/50 hover:bg-red-800 text-red-300 text-sm font-bold rounded-lg transition-colors"
                            type="button"
                            title="Alles wissen"
                        >
                            🗑️ Reset
                        </button>
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
}
